import type { DeductionSignal } from "../data/projects";
import type { AccHub, AccProject } from "../services/dataManagement";

export interface InferredAccProfile {
  locationLabel: string;
  county?: string;
  codeBasis: string;
  projectType: string;
  phase: string;
  metadataSource: string;
  jurisdictionIds: string[];
  confidence: "high" | "low";
  deductionTrace: DeductionSignal[];
}

export interface InferAccProfileInput {
  hub: AccHub;
  project: AccProject;
  platform?: string;
  selectedFileName?: string;
  folderPath?: string;
}

/**
 * Heuristic jurisdiction inference from ACC project + hub names.
 * Phase 2c placeholder until ACC exposes structured address fields.
 */
export function inferAccProjectProfile(
  input: InferAccProfileInput
): InferredAccProfile {
  const corpus = [
    input.project.name,
    input.hub.name,
    input.selectedFileName ?? "",
    input.folderPath ?? "",
  ]
    .join(" ")
    .toLowerCase();

  const trace: DeductionSignal[] = [
    { label: "ACC project name", value: input.project.name },
    { label: "ACC hub", value: input.hub.name },
  ];
  if (input.hub.region) {
    trace.push({ label: "APS hub region", value: input.hub.region });
  }
  if (input.platform) {
    trace.push({ label: "Platform", value: input.platform });
  }
  if (input.selectedFileName) {
    trace.push({ label: "Selected model", value: input.selectedFileName });
  }

  if (/\bbca\b|singapore/.test(corpus)) {
    trace.push({
      label: "Name signal",
      value: "BCA / Singapore keyword in project or hub",
    });
    return {
      locationLabel: "Singapore",
      codeBasis: "Singapore BCA Code on Accessibility (CABE)",
      projectType: "ACC design project",
      phase: "Design / permit package assembly",
      metadataSource: "ACC Data Management · hub region",
      jurisdictionIds: ["sg-bca"],
      confidence: "high",
      deductionTrace: trace,
    };
  }

  if (
    /\bla[\s-]?foothill|\bcasita\b|los angeles|\bla county\b|\bl\.a\./.test(
      corpus
    )
  ) {
    trace.push({
      label: "Name signal",
      value: "Los Angeles / LA County keyword in project or model",
    });
    return {
      locationLabel: "Los Angeles, CA",
      county: "Los Angeles County",
      codeBasis:
        "2022 California Residential Code (Title 24 Part 2.5, LA County)",
      projectType: "Detached single-family ADU",
      phase: "Design / permit package assembly",
      metadataSource: "ACC Data Management · hub region",
      jurisdictionIds: ["us-ca-la-county"],
      confidence: "high",
      deductionTrace: trace,
    };
  }

  if (
    /\bnew york\b|\bnyc\b|\bsnowdon\b|\bsustainable villa\b|\bmixed.?use\b/.test(
      corpus
    )
  ) {
    trace.push({
      label: "Name signal",
      value: "New York / NYC keyword in project or hub",
    });
    return {
      locationLabel: "New York, NY",
      county: "New York City",
      codeBasis: "2022 NYC Building Code (on 2018 IBC base)",
      projectType: "Mixed-use residential over retail",
      phase: "Permit package assembly",
      metadataSource: "ACC Data Management · hub region",
      jurisdictionIds: ["us-ny-nyc"],
      confidence: "high",
      deductionTrace: trace,
    };
  }

  if (input.hub.region === "EMEA") {
    trace.push({
      label: "Region fallback",
      value: "EMEA hub — no city keyword matched",
    });
    return {
      locationLabel: "EMEA",
      codeBasis: "Jurisdiction not inferred — review project metadata",
      projectType: "ACC design project",
      phase: "Design / permit package assembly",
      metadataSource: "ACC Data Management · hub region",
      jurisdictionIds: [],
      confidence: "low",
      deductionTrace: trace,
    };
  }

  trace.push({
    label: "Region fallback",
    value: "US hub — no city/county keyword matched in project name",
  });
  return {
    locationLabel: "United States",
    codeBasis: "Jurisdiction not inferred — select Evaluate under manually",
    projectType: "ACC design project",
    phase: "Design / permit package assembly",
    metadataSource: "ACC Data Management · hub region",
    jurisdictionIds: [],
    confidence: "low",
    deductionTrace: trace,
  };
}
